import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const navLinks = [
  { label: "My Story", href: "#my-story" },
  { label: "How I Help", href: "#services" },
  { label: "The Method", href: "#method" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "FAQ", href: "#faq" },
  { label: "Blog", href: "/blog" },
  { label: "Course", href: "/course" },
];

const externalLinks = [
  { label: "Instagram", href: "https://www.instagram.com/energysafemethod", icon: "ri-instagram-line" },
  { label: "Linktree", href: "https://linktr.ee/HealingBreakthroughCoaching", icon: "ri-links-line" },
  { label: "Buy Me a Coffee", href: "https://buymeacoffee.com/federicaten", icon: "ri-cup-line" },
  { label: "Email", href: "mailto:federica.tedeschi3@gmail.com", icon: "ri-mail-line" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/' || location.pathname === '';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith("#")) {
      e.preventDefault();
      navigate(`/${href}`);
      setMobileOpen(false);
    } else if (href === '/') {
      e.preventDefault();
      if (isHomePage) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        navigate('/');
      }
      setMobileOpen(false);
    } else if (href.startsWith("/")) {
      e.preventDefault();
      navigate(href);
      setMobileOpen(false);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 bg-[#2C3E6B]/95 backdrop-blur-md transition-shadow duration-500 ease-out ${
        scrolled
          ? "shadow-[0_10px_34px_-12px_rgba(0,0,0,0.55)]"
          : "shadow-[0_1px_0_0_rgba(255,255,255,0.08)]"
      }`}
    >
      {/* Bottom hairline — a faint shimmer that eases in once the page scrolls */}
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-x-0 -bottom-px h-px transition-opacity duration-700 ease-out ${
          scrolled ? "opacity-100" : "opacity-0"
        }`}
      >
        <div className="nav-hairline h-px w-full" />
      </div>
      <div className="max-w-6xl mx-auto px-6 flex items-center justify-between h-[72px]">
        <a href="/" onClick={(e) => handleNavClick(e, '/')} className="flex items-center gap-2.5 cursor-pointer">
          <img
            alt="Healing & Breakthrough Coaching"
            className="w-12 h-12 object-contain hover:scale-[1.06] hover:-translate-y-[3px] transition-all duration-300 ease-out"
            src="https://storage.readdy-site.link/project_files/185c4543-d0dc-49d0-8dbb-59fc7deb7f59/c0cd9c52-4c80-4827-8208-f750b5440eb5_Logo-.png?v=908c6ba8c809d066ed81cd723f530f12"
          />
          <span className="font-heading text-lg font-semibold hidden sm:inline text-white">
            Healing &amp; Breakthrough
          </span>
        </a>

        {/* Desktop Nav */}
        <div className="hidden lg:grid grid-cols-[1fr_auto] grid-rows-2 gap-x-5 gap-y-1 items-center">
          <div className="flex items-center gap-5">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-sm font-medium transition-colors cursor-pointer whitespace-nowrap text-white/75 hover:text-white"
              >
                {link.label}
              </a>
            ))}
          </div>
          <a
            href="https://calendly.com/federicatedeschi3/new-meeting"
            target="_blank"
            rel="noopener noreferrer"
            className="row-span-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ease-out whitespace-nowrap cursor-pointer hover:scale-[1.04] hover:-translate-y-[3px] hover:shadow-md self-center bg-white text-[#2C3E6B] hover:bg-white/90"
          >
            Book a Free Conversation
          </a>
          <div className="flex items-center justify-end gap-5">
            {externalLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap text-white/75 hover:text-white"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${link.icon} text-sm`} />
                </span>
                {link.label}
              </a>
            ))}
          </div>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="lg:hidden p-2 cursor-pointer"
          aria-label="Toggle menu"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-white text-xl`} />
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-[#2C3E6B]/98 backdrop-blur-md border-t border-white/10 px-6 py-4 space-y-3">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="block text-sm font-medium transition-colors text-white/90 hover:text-white"
            >
              {link.label}
            </a>
          ))}
          {externalLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-sm font-medium transition-colors text-white/80 hover:text-white"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={`${link.icon} text-sm`} />
              </span>
              {link.label}
            </a>
          ))}
          <a
            href="https://calendly.com/federicatedeschi3/new-meeting"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap bg-white text-[#2C3E6B] hover:bg-white/90"
          >
            Book a Free Conversation
          </a>
        </div>
      )}
    </nav>
  );
}